import React, { forwardRef, JSX, Ref, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { FlatIndexLocationWithAlign, ListRange, Virtuoso, VirtuosoHandle } from '../../atoms';

interface RenderInsertZone {
  virtualizedListIndex: number;
  originalListIndex: number;
  insertItem: (index: number) => void;
  hoverIndex: number | null;
  setHoverIndex: React.Dispatch<React.SetStateAction<number | null>>;
}

interface InsertableListVirtuosoProps<T> {
  items: T[];
  onChange: (index: number) => void;
  handleSetActiveItemIndex: (index: number | null) => void;
  renderHeader?: () => React.ReactNode;
  renderItem: (item: T, index: number) => React.ReactNode;
  renderInsertZone: (props: RenderInsertZone) => React.ReactNode;
  style?: React.CSSProperties;
}

export interface InsertableListVirtuosoRef {
  scrollToIndex: (location: FlatIndexLocationWithAlign | number) => void;
}

type InsertableListVirtuosoComponent = <T>(
  props: InsertableListVirtuosoProps<T> & { ref?: Ref<InsertableListVirtuosoRef> },
) => JSX.Element;

/** similar to InsertableListVirtualized component but with dynamic items height */
export const InsertableListVirtuoso = forwardRef(
  <T,>(props: InsertableListVirtuosoProps<T>, ref: Ref<InsertableListVirtuosoRef>) => {
    const { items, onChange, handleSetActiveItemIndex, renderHeader, renderItem, renderInsertZone, style } = props;

    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    /** Flag to track programmatic scrolls */
    const isProgrammaticScroll = useRef<boolean>(false);

    const virtuosoRef = useRef<VirtuosoHandle>(null);

    useImperativeHandle(
      ref,
      () => ({
        scrollToIndex: (options) => {
          isProgrammaticScroll.current = true;
          virtuosoRef.current?.scrollToIndex(options);
        },
      }),
      [],
    );

    const insertItem = useCallback(
      (index: number) => {
        onChange(index);
        setHoverIndex(null);
      },
      [onChange],
    );

    /** Rows alternate: insert zone, item, insert zone, ..., insert zone. */
    const totalCount = items.length * 2 + 1;

    const itemContent = (index: number) => {
      if (index % 2 === 0) {
        // Insert zone
        const originalInsertIndex = index / 2;
        return (
          <div data-key={`insert-zone-${originalInsertIndex}-of-${index}`} className="space-y-0">
            {renderInsertZone({
              virtualizedListIndex: index,
              originalListIndex: originalInsertIndex,
              insertItem: insertItem,
              hoverIndex: hoverIndex,
              setHoverIndex: setHoverIndex,
            })}
          </div>
        );
      } else {
        // Item
        const originalItemIndex = (index - 1) / 2;
        return (
          <div data-key={`item-${originalItemIndex}-of-${index}`} className="space-y-0">
            {renderItem(items[originalItemIndex], index)}
          </div>
        );
      }
    };

    const handleRangeChanged = useCallback(
      (range: ListRange) => {
        if (!isProgrammaticScroll.current) {
          handleSetActiveItemIndex(
            /** skip insert zones */
            Math.floor(range.startIndex / 2),
          );
        }
      },
      [handleSetActiveItemIndex],
    );

    const disableProgrammaticScroll = () => {
      if (isProgrammaticScroll.current) {
        isProgrammaticScroll.current = false;
      }
    };

    return (
      <div className="bg-fletchers-white h-full w-full">
        {!!renderHeader && <div>{renderHeader()}</div>}
        <Virtuoso
          ref={virtuosoRef}
          totalCount={totalCount}
          itemContent={itemContent}
          rangeChanged={handleRangeChanged}
          onMouseOver={disableProgrammaticScroll}
          onTouchStart={disableProgrammaticScroll}
          style={{ height: '100%', ...style }}
        />
      </div>
    );
  },
) as InsertableListVirtuosoComponent;
